import { BoardNodeHandles } from "./board-node-handles";
import { NodeResizer, type NodeProps } from "@xyflow/react";
import { useCanvasEditability, useCanvasEditSession, useCanvasResizeSession } from "../../lib/canvas-editability";
import { patchNodeData } from "../../lib/canvas-commands";
import type { CircleNodeData } from "../../types/board";
import { getContrastTextColor } from "../../lib/color-utils";

export function CircleNode({ id, data, selected, isConnectable }: NodeProps) {
  const nodeData = data as unknown as CircleNodeData;
  const { canEditDocument } = useCanvasEditability();
  const edit = useCanvasEditSession("text");
  const resize = useCanvasResizeSession();
  const updateLabel = (label: string) => {
    if (label !== (nodeData.label ?? "")) edit.update(patchNodeData(id, { label }));
  };

  const textColor = getContrastTextColor(nodeData.bgColor);
  const fontSize = nodeData.fontSize || 14;

  return (
    <>
      <NodeResizer
        {...resize}
        isVisible={!!selected && canEditDocument}
        minWidth={80}
        minHeight={80}
        keepAspectRatio
        lineClassName="!border-primary !rounded-full"
        handleClassName="!h-2.5 !w-2.5 !rounded-full !border-2 !border-primary !bg-background"
      />
      <BoardNodeHandles type="circle" isConnectable={isConnectable} />
      <div
        className="flex items-center justify-center rounded-full border-2"
        style={{
          width: "100%",
          aspectRatio: "1 / 1",
          minWidth: 80,
          minHeight: 80,
          backgroundColor: nodeData.bgColor || "var(--color-card)",
          borderColor: nodeData.borderColor || "var(--color-border)",
          color: textColor,
          fontSize,
        }}
        onDoubleClick={edit.begin}
      >
        {edit.active ? (
          <input
            autoFocus
            className="nodrag nowheel w-16 bg-transparent text-center outline-none"
            style={{ color: textColor, fontSize }}
            defaultValue={nodeData.label}
            onBlur={(e) => {
              updateLabel(e.target.value);
              edit.finish();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                updateLabel((e.target as HTMLInputElement).value);
                edit.finish();
              }
            }}
          />
        ) : (
          <span className="min-h-0 min-w-0 max-h-[70%] w-[70%] overflow-hidden whitespace-pre-wrap text-center [overflow-wrap:anywhere]">{nodeData.label ?? ""}</span>
        )}
      </div>
    </>
  );
}
